import { useState, useEffect, useCallback } from 'react';
import {
  useCouponsForMails,
  useSendMails,
  useSendDebtMails,
} from '../../../hooks/useSubscribers';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { ITEMS_PER_PAGE } from '../constants/couponsMailsConstants';

const getCurrentMonth = () => (new Date().getMonth() + 1).toString();
const getCurrentQuarter = () => Math.ceil((new Date().getMonth() + 1) / 3).toString();

export const useCouponsMailsTable = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  const [page, setPage] = useState(1);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [bulkConfirmationDialogOpen, setBulkConfirmationDialogOpen] = useState(false);
  const [emailResultsDialogOpen, setEmailResultsDialogOpen] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState({});
  const [emailResults, setEmailResults] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [includeAllSubscribers, setIncludeAllSubscribers] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  // Estados separados para los filtros del formulario y la búsqueda
  const [filter, setFilter] = useState({
    timePeriod: 'month',
    quarter: '',
    month: getCurrentMonth(),
    year: new Date().getFullYear().toString(),
    plan: '',
    group: '',
    order: '',
    notCancelled: true,
  });

  const [appliedFilters, setAppliedFilters] = useState({});

  // Actualizar filtros cuando plans esté disponible
  useEffect(() => {
    if (plans.length > 0 && !filter.plan) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        plan: plans[0].uuid,
      }));
    }
  }, [plans, filter.plan]);

  // Query unificada para obtener datos según los filtros aplicados
  const { data: couponsResponse, isLoading: isCouponsLoading } = useCouponsForMails(
    appliedFilters,
    { enabled: searchTriggered && Object.keys(appliedFilters).length > 0 }
  );

  const sendMailsMutation = useSendMails();
  const sendDebtMailsMutation = useSendDebtMails();

  // Datos procesados
  const data = couponsResponse?.subscribers || [];
  const totalPages = couponsResponse?.pages || 0;
  const totalCount = couponsResponse?.total || 0;

  const isLoading = isCouponsLoading || sendMailsMutation.isPending;

  const handleFilter = useCallback((event) => {
    const { name, value, checked, type } = event.target;

    setFilter((prevFilter) => {
      let newFilter = {
        ...prevFilter,
        [name]: type === 'checkbox' ? checked : value,
      };

      // Resetear filtros relacionados con período de tiempo
      if (name === 'timePeriod') {
        if (value === 'quarter') {
          newFilter = { ...newFilter, month: '', quarter: getCurrentQuarter() };
        } else if (value === 'month') {
          newFilter = { ...newFilter, quarter: '', month: getCurrentMonth() };
        }
      }

      return newFilter;
    });
  }, []);

  const handleSearch = useCallback(() => {
    // Preparar filtros para la API
    const apiFilter = { ...filter, page: 1, per_page: rowsPerPage };

    // Transformar 'order' a 'contract_number' para el backend
    if (apiFilter.order) {
      apiFilter.contract_number = apiFilter.order;
      delete apiFilter.order;
    }

    // Limpiar solo filtros completamente vacíos, mantener 'contract_number' y otros valores válidos
    Object.keys(apiFilter).forEach((key) => {
      if (apiFilter[key] === '' || apiFilter[key] === null || apiFilter[key] === undefined) {
        delete apiFilter[key];
      }
    });

    // Manejar casos especiales
    if (apiFilter.group === 'todos' || apiFilter.group === 'all') {
      delete apiFilter.group; // Eliminar filtro de grupo para obtener todos
    }


    // Aplicar los filtros para disparar las queries
    setAppliedFilters(apiFilter);
    setSearchTriggered(true);
    setPage(1);
  }, [filter, rowsPerPage]);

  const handlePageChange = useCallback(
    (event, value) => {
      setPage(value);

      // Actualizar los filtros aplicados con la nueva página
      if (Object.keys(appliedFilters).length > 0) {
        setAppliedFilters((prev) => ({
          ...prev,
          page: value,
          per_page: rowsPerPage,
        }));
      }
    },
    [appliedFilters, rowsPerPage]
  );

  const handleRowsPerPageChange = useCallback(
    (event) => {
      const value = parseInt(event.target.value, 10);
      if (!Number.isNaN(value)) {
        setRowsPerPage(value);
        setPage(1);
        if (Object.keys(appliedFilters).length > 0) {
          setAppliedFilters((prev) => ({
            ...prev,
            page: 1,
            per_page: value,
          }));
          setSearchTriggered(true);
        }
      }
    },
    [appliedFilters]
  );

  const handleSendEmail = useCallback((emailData) => {
    setSelectedEmailData(emailData);
    setEmailDialogOpen(true);
  }, []);

  const handleSelectItem = useCallback((item) => {
    setSelectedItems((prevSelectedItems) => {
      const isSelected = prevSelectedItems.some(
        (selectedItem) => selectedItem.subscriber_uuid === item.subscriber_uuid
      );

      if (isSelected) {
        return prevSelectedItems.filter(
          (selectedItem) => selectedItem.subscriber_uuid !== item.subscriber_uuid
        );
      } else {
        return [...prevSelectedItems, item];
      }
    });
  }, []);

  const handleIncludeAllSubscribersChange = useCallback((event) => {
    const checked = event.target.checked;
    setIncludeAllSubscribers(checked);

    if (!checked) {
      setSelectedItems([]);
    }
  }, []);

  const handleBulkSend = useCallback(() => {
    setBulkConfirmationDialogOpen(true);
  }, []);

  const handleBulkSendConfirmation = useCallback(
    async (confirmedEmails) => {
      try {
        let result;
        // Si NO está seleccionado "No cancelados", enviar a send-debt
        if (!filter.notCancelled) {
          result = await sendDebtMailsMutation.mutateAsync(confirmedEmails);
        } else {
          result = await sendMailsMutation.mutateAsync(confirmedEmails);
        }
        
        setEmailResults(result);
        setEmailResultsDialogOpen(true);
        
        setBulkConfirmationDialogOpen(false);
        setSelectedItems([]);
      } catch (error) {
        console.error('Error sending bulk emails:', error);
        
        const errorResult = {
          message: "Error al enviar los emails",
          summary: {
            total_emails: confirmedEmails?.emails?.length || 0,
            successful_count: 0,
            failed_count: confirmedEmails?.emails?.length || 0
          },
          successful_emails: [],
          failed_emails: confirmedEmails?.emails?.map(email => ({
            email: email.name || 'Desconocido',
            order_number: email.order_number || 'Desconocido',
            uuid_for_file: email.uuid_for_file || 'Desconocido',
            error: error.message || 'Error desconocido'
          })) || []
        };
        
        setEmailResults(errorResult);
        setEmailResultsDialogOpen(true);
        setBulkConfirmationDialogOpen(false);
      }
    },
    [sendMailsMutation, sendDebtMailsMutation, filter.notCancelled]
  );

  const handleCloseEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    setSelectedEmailData({});
  }, []);

  const handleCloseBulkConfirmationDialog = useCallback(() => {
    setBulkConfirmationDialogOpen(false);
    setSelectedItems([]);
  }, []);

  const handleCloseEmailResultsDialog = useCallback(() => {
    setEmailResultsDialogOpen(false);
    setEmailResults(null);
  }, []);

  const handleIndividualEmailResult = useCallback((result) => {
    setEmailResults(result);
    setEmailResultsDialogOpen(true);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  useEffect(() => {
    if (!bulkConfirmationDialogOpen) {
      clearSelection();
    }
  }, [bulkConfirmationDialogOpen, clearSelection]);

  return {
    // Datos principales
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,

    // Estados de diálogos
    emailDialogOpen,
    bulkConfirmationDialogOpen,
    emailResultsDialogOpen,
    selectedEmailData,
    emailResults,

    // Estados de selección
    selectedItems,
    includeAllSubscribers,

    // Datos externos
    plans,
    groups,

    // Handlers
    handleFilter,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleSendEmail,
    handleSelectItem,
    handleIncludeAllSubscribersChange,
    handleBulkSend,
    handleBulkSendConfirmation,
    handleCloseEmailDialog,
    handleCloseBulkConfirmationDialog,
    handleCloseEmailResultsDialog,
    handleIndividualEmailResult,
  };
};
